import { useState } from 'react';
import Modal from '../shared/Modal';
import { upsertScheduleItem } from '../../utils/api';
import { TEAMS } from '../../config';

const DAYS = [
  'Thursday', 'Friday', 'Saturday', 'Sunday',
  'Monday', 'Tuesday', 'Wednesday',
];

export default function AddScheduleItemModal({ open, onClose, onSave, showToast }) {
  const [day, setDay] = useState('Thursday');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [activity, setActivity] = useState('');
  const [location, setLocation] = useState('');
  const [appliesTo, setAppliesTo] = useState(''); // force an explicit choice — no accidental "All Teams"
  const [specificTeams, setSpecificTeams] = useState([]);
  const [isMeal, setIsMeal] = useState('No');
  const [isTransport, setIsTransport] = useState('No');
  const [saving, setSaving] = useState(false);

  function toggleTeam(team) {
    setSpecificTeams(prev =>
      prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]
    );
  }

  function reset() {
    setDay('Thursday'); setDate(''); setStart(''); setEnd('');
    setActivity(''); setLocation(''); setAppliesTo('');
    setSpecificTeams([]); setIsMeal('No'); setIsTransport('No');
  }

  async function handleSave() {
    if (!activity.trim()) { showToast('Item name is required.'); return; }
    if (!appliesTo) { showToast('Choose who this applies to (All Teams or specific teams).'); return; }
    if (appliesTo === 'specific' && specificTeams.length === 0) {
      showToast('Select at least one team, or choose All Teams.'); return;
    }
    setSaving(true);
    try {
      // Never silently coerce an unselected item to ALL — that broadcast it to
      // every servant leader. Applies only to what the admin explicitly picked.
      const teamsValue = appliesTo === 'ALL'
        ? (isTransport === 'Yes' ? 'ALL, Transportation' : 'ALL')
        : specificTeams.join(', ');

      const item = {
        day, date, start, end,
        activity: activity.trim(),
        location: location.trim(),
        team: teamsValue,
        appliesToTeams: teamsValue,
        miscPersonnel: '',
        katTeam: '',
        isMeal: isMeal !== 'No',
        createdAt: new Date().toISOString(),
      };
      await upsertScheduleItem(item);
      showToast('Schedule item added.');
      reset();
      onClose();
      if (onSave) onSave();
    } catch (err) {
      showToast('Error saving item.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Add Schedule Item"
      footer={
        <>
          <button className="btn" onClick={() => { reset(); onClose(); }}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Add Item'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
        <div>
          <label className="form-label">Day</label>
          <select className="form-input" value={day} onChange={e => setDay(e.target.value)}>
            {DAYS.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Date</label>
          <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
        <div><label className="form-label">Start Time</label><input className="form-input" type="time" value={start} onChange={e => setStart(e.target.value)} /></div>
        <div><label className="form-label">End Time</label><input className="form-input" type="time" value={end} onChange={e => setEnd(e.target.value)} /></div>
      </div>
      <div className="form-group">
        <label className="form-label">Session / Item Name</label>
        <input className="form-input" value={activity} onChange={e => setActivity(e.target.value)} placeholder="e.g. General Session 1" />
      </div>
      <div className="form-group">
        <label className="form-label">Location</label>
        <input className="form-input" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Main Auditorium" />
      </div>
      <div className="form-group">
        <label className="form-label">Applies To</label>
        <select className="form-input" value={appliesTo} onChange={e => setAppliesTo(e.target.value)}>
          <option value="">— Choose who this applies to —</option>
          <option value="ALL">All Teams</option>
          <option value="specific">Specific Teams…</option>
        </select>
      </div>
      {appliesTo === 'specific' && (
        <div className="form-group">
          <label className="form-label">Select Teams</label>
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
            {TEAMS.map(t => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                <input type="checkbox" checked={specificTeams.includes(t)} onChange={() => toggleTeam(t)} style={{ accentColor: 'var(--black)' }} />
                {t}
              </label>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
        <div>
          <label className="form-label">Meal?</label>
          <select className="form-input" value={isMeal} onChange={e => setIsMeal(e.target.value)}>
            <option>No</option><option>Breakfast</option><option>Lunch</option><option>Dinner</option>
          </select>
        </div>
        <div>
          <label className="form-label">Transportation?</label>
          <select className="form-input" value={isTransport} onChange={e => setIsTransport(e.target.value)}>
            <option>No</option><option>Yes</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}
